import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchemaIcon from "@mui/icons-material/Schema";
import RefreshIcon from "@mui/icons-material/Refresh";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function AdminContacts() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [contacts, setContacts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [refreshing, setRefreshing] = React.useState(false);
  const [pagination, setPagination] = React.useState({
    total: 0,
    limit: 20,
    offset: 0,
    page: 1,
  });
  const [schemaDialogOpen, setSchemaDialogOpen] = React.useState(false);
  const [schemaTableName, setSchemaTableName] = React.useState("");
  const [schemaData, setSchemaData] = React.useState(null);
  const [loadingSchema, setLoadingSchema] = React.useState(false);
  const [schemaError, setSchemaError] = React.useState(null);

  const fetchContacts = React.useCallback(async (search = "", offset = 0, limit = null, showRefresh = false) => {
    if (showRefresh) {
      setRefreshing(true);
      setLoading(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    const startTime = Date.now();
    
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const params = new URLSearchParams();
      if (search && search.trim()) {
        params.append("search", search.trim());
      }
      const limitToUse = limit !== null ? limit : pagination.limit;
      params.append("limit", limitToUse.toString());
      params.append("offset", offset.toString());

      const response = await fetch(`${API_BASE_URL}/admin/contacts?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch contacts: ${response.status}`);
      }

      const data = await response.json();
      
      // Ensure minimum 1 second delay when refreshing
      if (showRefresh) {
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }
      
      setContacts(data.data || []);
      const paginationData = data.pagination || {};
      const total = paginationData.total || 0;
      const currentOffset = paginationData.offset || offset;
      const limitUsed = limit !== null ? limit : pagination.limit;
      const page = Math.floor(currentOffset / limitUsed) + 1;
      
      setPagination((prev) => ({
        ...prev,
        total,
        offset: currentOffset,
        page,
        limit: limitUsed,
        hasMore: paginationData.hasMore || false,
      }));
    } catch (err) {
      console.error("Error fetching contacts:", err);
      setError(err.message || "Failed to fetch contacts");
      
      // Still ensure minimum delay even on error
      if (showRefresh) {
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }
    } finally {
      if (showRefresh) {
        setRefreshing(false);
      }
      setLoading(false);
    }
  }, [getAccessTokenSilently, pagination.limit]);

  React.useEffect(() => {
    fetchContacts(searchQuery, 0);
  }, []);

  // Debounced search
  const searchTimeoutRef = React.useRef(null);
  React.useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchContacts(searchQuery, 0);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, fetchContacts]);

  React.useEffect(() => {
    // Refetch when limit changes
    fetchContacts(searchQuery, 0, pagination.limit);
  }, [pagination.limit]);

  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * pagination.limit;
    fetchContacts(searchQuery, newOffset, pagination.limit);
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      limit: newLimit,
      offset: 0, // Reset to first page
      page: 1,
    }));
    // fetchContacts will be called by the useEffect watching pagination.limit
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const handleViewOrganization = (organizationId) => {
    if (organizationId) {
      navigate(`/dashboard/organizations/${organizationId}`);
    }
  };

  const handleFetchSchema = async () => {
    if (!schemaTableName || !schemaTableName.trim()) {
      setSchemaError("Table name is required");
      return;
    }

    setLoadingSchema(true);
    setSchemaError(null);
    setSchemaData(null);

    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const response = await fetch(`${API_BASE_URL}/admin/schema/${schemaTableName.trim()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to fetch schema" }));
        throw new Error(errorData.error || `Failed to fetch schema: ${response.status}`);
      }

      const data = await response.json();
      setSchemaData(data);
    } catch (err) {
      console.error("Error fetching schema:", err);
      setSchemaError(err.message || "Failed to fetch schema");
    } finally {
      setLoadingSchema(false);
    }
  };

  const handleCloseSchemaDialog = () => {
    setSchemaDialogOpen(false);
    setSchemaTableName("");
    setSchemaData(null);
    setSchemaError(null);
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Contacts
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Contacts"
              description="Manage all contacts in the system"
              icon={<ContactMailIcon sx={{ color: "primary.main" }} />}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                  onClick={() => fetchContacts(searchQuery, pagination.offset, pagination.limit, true)}
                  disabled={refreshing}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  sx={{ bgcolor: "white", color: "black", "&:hover": { bgcolor: "grey.100" } }}
                  startIcon={<SchemaIcon />}
                  onClick={async () => {
                    setSchemaDialogOpen(true);
                    setSchemaTableName("contacts");
                    setSchemaData(null);
                    setSchemaError(null);
                    setLoadingSchema(true);
                    setSchemaError(null);
                    try {
                      const token = await getAccessTokenSilently().catch(() => null);
                      const response = await fetch(`${API_BASE_URL}/admin/schema/contacts`, {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                      });

                      if (!response.ok) {
                        const errorData = await response.json().catch(() => ({ error: "Failed to fetch schema" }));
                        throw new Error(errorData.error || `Failed to fetch schema: ${response.status}`);
                      }

                      const data = await response.json();
                      setSchemaData(data);
                    } catch (err) {
                      console.error("Error fetching schema:", err);
                      setSchemaError(err.message || "Failed to fetch schema");
                    } finally {
                      setLoadingSchema(false);
                    }
                  }}
                >
                  Schema
                </Button>
              </Box>
            </AdminPageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Card>
              <CardContent>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Skeleton height={20} width={180} />
                      <Skeleton height={40} width={120} borderRadius={1} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Skeleton height={32} width={80} borderRadius={1} />
                      <Skeleton height={20} width={150} />
                      <Skeleton height={32} width={60} borderRadius={1} />
                    </Box>
                  </Box>
                ) : pagination.total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, pagination.total)} of{" "}
                        {pagination.total} contacts
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1 || loading}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Page {pagination.page} of {totalPages} ({pagination.total} total)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= totalPages || loading}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search contacts by name, email, or organization..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Organization</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        Array.from({ length: pagination.limit }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }} align="right">
                              <Skeleton height={32} width={80} />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : contacts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">
                              {searchQuery ? "No contacts found matching your search" : "No contacts found"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        contacts.map((contact) => (
                          <TableRow key={contact.id}>
                            <TableCell sx={{ py: 1.5 }}>
                              {contact.first_name || ""} {contact.last_name || ""}
                              {contact.role && `, ${contact.role}`}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>{contact.email || "N/A"}</TableCell>
                            <TableCell sx={{ py: 1.5 }}>{contact.phone || contact.mobile || "N/A"}</TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              {contact.organization_name || "N/A"}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }} align="right">
                              {contact.organization_id && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => handleViewOrganization(contact.organization_id)}
                                >
                                  View Org
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Skeleton height={20} width={180} />
                      <Skeleton height={40} width={120} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Skeleton height={32} width={80} borderRadius={1} />
                      <Skeleton height={20} width={150} />
                      <Skeleton height={32} width={60} borderRadius={1} />
                    </Box>
                  </Box>
                ) : pagination.total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, pagination.total)} of{" "}
                        {pagination.total} contacts
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1 || loading}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Page {pagination.page} of {totalPages} ({pagination.total} total)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= totalPages || loading}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Squarespace websites are attached to a contact.</strong> The Squarespace customer ID is stored in{" "}
                <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.9em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                  contact.meta_data.squarespace_customer_id
                </Box>
                {" "}— this links the contact to their Squarespace account when a website order is created.
              </Typography>
            </Alert>
          </Container>
          <Footer />
        </Box>
      </Box>

      {/* Schema Dialog */}
      <Dialog
        open={schemaDialogOpen}
        onClose={handleCloseSchemaDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Table Schema</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Table Name</InputLabel>
              <Select
                value={schemaTableName}
                onChange={(e) => {
                  setSchemaTableName(e.target.value);
                  // Automatically fetch schema when selection changes
                  if (e.target.value) {
                    const fetchSchema = async () => {
                      setLoadingSchema(true);
                      setSchemaError(null);
                      setSchemaData(null);
                      try {
                        const token = await getAccessTokenSilently().catch(() => null);
                        const response = await fetch(`${API_BASE_URL}/admin/schema/${e.target.value}`, {
                          headers: token ? { Authorization: `Bearer ${token}` } : {},
                        });

                        if (!response.ok) {
                          const errorData = await response.json().catch(() => ({ error: "Failed to fetch schema" }));
                          throw new Error(errorData.error || `Failed to fetch schema: ${response.status}`);
                        }

                        const data = await response.json();
                        setSchemaData(data);
                      } catch (err) {
                        console.error("Error fetching schema:", err);
                        setSchemaError(err.message || "Failed to fetch schema");
                      } finally {
                        setLoadingSchema(false);
                      }
                    };
                    fetchSchema();
                  }
                }}
                label="Table Name"
              >
                <MenuItem value="organizations">organizations</MenuItem>
                <MenuItem value="users">users</MenuItem>
                <MenuItem value="subscriptions">subscriptions</MenuItem>
                <MenuItem value="orders">orders</MenuItem>
                <MenuItem value="products">products</MenuItem>
                <MenuItem value="contacts">contacts</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {schemaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {schemaError}
            </Alert>
          )}

          {loadingSchema && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {schemaData && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Table: {schemaData.table_name}
              </Typography>
              
              {/* Columns */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Columns
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Nullable</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Position</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schemaData.columns.map((col) => (
                      <TableRow key={col.name}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={schemaData.primary_keys?.includes(col.name) ? "bold" : "normal"}>
                            {col.name}
                            {schemaData.primary_keys?.includes(col.name) && " (PK)"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {col.type}
                            {col.max_length && `(${col.max_length})`}
                            {col.precision && col.scale && `(${col.precision},${col.scale})`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={col.nullable ? "YES" : "NO"}
                            size="small"
                            color={col.nullable ? "default" : "warning"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                            {col.default || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{col.position}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Foreign Keys */}
              {schemaData.foreign_keys && schemaData.foreign_keys.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Foreign Keys
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Column</TableCell>
                          <TableCell>References Table</TableCell>
                          <TableCell>References Column</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schemaData.foreign_keys.map((fk, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{fk.column}</TableCell>
                            <TableCell>{fk.references_table}</TableCell>
                            <TableCell>{fk.references_column}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Unique Constraints */}
              {schemaData.unique_constraints && schemaData.unique_constraints.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Unique Constraints
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {schemaData.unique_constraints.map((uc, idx) => (
                      <Chip
                        key={idx}
                        label={`${uc.column} (${uc.constraint_name})`}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </>
              )}

              {/* Indexes */}
              {schemaData.indexes && schemaData.indexes.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Indexes
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Definition</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schemaData.indexes.map((idx) => (
                          <TableRow key={idx.name}>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">
                                {idx.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                                {idx.definition}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Raw JSON View */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Raw JSON
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      overflow: "auto",
                      maxHeight: "300px",
                      m: 0,
                    }}
                  >
                    {JSON.stringify(schemaData, null, 2)}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSchemaDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
